import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getRequestsApi, getBrandsApi, getSlotsApi, getCohortsApi, updateRequestApi, deleteRequestApi } from '../../utils/api';
import { formatDate } from '../../utils/helpers';
import { Clock, CheckCircle, XCircle, Edit2, Download, Trash2 } from 'lucide-react';

const MyRequests = ({ showToast, selectedBrand, isViewOnly }) => {
  const { user } = useAuth();
  const isViewer = user?.role === 'viewer';
  const [requests, setRequests] = useState([]);
  const [brands, setBrands] = useState([]);
  const [slots, setSlots] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterBrand, setFilterBrand] = useState('all');

  useEffect(() => {
    loadData();
  }, [user, selectedBrand]);

  const loadData = async () => {
    try {
      // For viewers, fetch by selectedBrand instead of user.id (they may have created
      // requests under multiple brands and need to see all those for the active brand)
      const requestsPromise = isViewer && selectedBrand
        ? getRequestsApi(null, selectedBrand)
        : getRequestsApi(user.id);

      const [requestsRes, brandsRes, slotsRes, cohortsRes] = await Promise.all([
        requestsPromise,
        getBrandsApi(),
        getSlotsApi(),
        getCohortsApi()
      ]);
      
      // Safely set data with fallback to prevent data loss
      setRequests(Array.isArray(requestsRes.data) ? requestsRes.data : []);
      setBrands(Array.isArray(brandsRes.data) ? brandsRes.data : []);
      setSlots(Array.isArray(slotsRes.data) ? slotsRes.data : []);
      setCohorts(Array.isArray(cohortsRes.data) ? cohortsRes.data : []);
      
      // Log for debugging
      console.log(`✅ Loaded ${requestsRes.data?.length || 0} requests for user ${user.id}`);
    } catch (error) {
      console.error('❌ Error loading data:', error);
      showToast(error.response?.data?.detail || 'Failed to load requests', 'error');
      // Keep existing data on error - don't reset to empty
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (request) => {
    // Store request in localStorage for editing
    localStorage.setItem('editRequest', JSON.stringify(request));
    // Navigate to Request Campaign page
    window.location.href = '#request-campaign';
    window.location.reload();
  };

  const handleResend = async (requestId) => {
    try {
      // Update status back to Pending
      await updateRequestApi(requestId, { status: 'Pending' });
      showToast('Request resubmitted successfully!', 'success');
      loadData(); // Reload requests
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to resend request', 'error');
    }
  };

  const handleDelete = async (request) => {
    const label = request.campaign_name || 'this request';
    if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return;
    try {
      await deleteRequestApi(request.id);
      showToast('Request deleted', 'success');
      setRequests((prev) => prev.filter((r) => r.id !== request.id));
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to delete request', 'error');
    }
  };

  const handleDownloadCsv = (csvContent, requestId) => {
    if (!csvContent) return;
    
    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cohort_${requestId}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Approved':
        return <CheckCircle className="text-green-500" size={20} />;
      case 'Rejected':
        return <XCircle className="text-red-500" size={20} />;
      default:
        return <Clock className="text-yellow-500" size={20} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'Rejected':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      default:
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    }
  };
  
  // Filter requests
  const filteredRequests = requests.filter(req => {
    // Search filter (campaign name, brand name, cohort name)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const brand = brands.find(b => b.id === req.brand_id);
      const cohort = cohorts.find(c => c.id === req.cohort_id);
      const matchesName = req.campaign_name?.toLowerCase().includes(query);
      const matchesBrand = brand?.name?.toLowerCase().includes(query);
      const matchesCohort = cohort?.name?.toLowerCase().includes(query);
      
      if (!matchesName && !matchesBrand && !matchesCohort) return false;
    }
    
    // Date range filter
    if (startDate && req.date < startDate) return false;
    if (endDate && req.date > endDate) return false;
    
    // Brand filter
    if (filterBrand !== 'all' && req.brand_id !== filterBrand) return false;
    
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">My Campaign Requests</h1>
        <p className="text-slate-400">Track the status of all campaigns you've submitted</p>
      </div>
      
      {/* Filters */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Search */}
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by campaign, brand, or cohort..."
          className="px-4 py-2 bg-slate-800 text-white rounded-xl border border-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 hover:border-slate-600 transition-all"
        />
        
        {/* Start Date */}
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          placeholder="Start Date"
          className="px-4 py-2 bg-slate-800 text-white rounded-xl border border-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 hover:border-slate-600 transition-all"
        />
        
        {/* End Date */}
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          placeholder="End Date"
          className="px-4 py-2 bg-slate-800 text-white rounded-xl border border-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 hover:border-slate-600 transition-all"
        />
        
        {/* Brand Filter */}
        <select
          value={filterBrand}
          onChange={(e) => setFilterBrand(e.target.value)}
          className="px-4 py-2 bg-slate-800 text-white rounded-xl border border-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 hover:border-slate-600 transition-all"
        >
          <option value="all">All Brands</option>
          {brands.map(brand => (
            <option key={brand.id} value={brand.id}>{brand.name}</option>
          ))}
        </select>
      </div>

      {/* Requests List */}
      {filteredRequests.length === 0 ? (
        <div className="bg-slate-800 rounded-lg p-12 text-center">
          <p className="text-slate-400">
            {requests.length === 0 ? 'No campaign requests yet' : 'No requests match your filters'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredRequests.map((request) => {
            const brand = brands.find(b => b.id === request.brand_id);
            const slot = slots.find(s => s.id === request.slot_id);
            const cohort = cohorts.find(c => c.id === request.cohort_id);

            return (
              <div key={request.id} className="bg-slate-800 rounded-lg overflow-hidden">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-4 flex-1">
                      {/* Creative thumbnail */}
                      {request.creative && (
                        <img
                          src={request.creative}
                          alt="Campaign creative"
                          className="w-24 h-24 object-cover rounded-lg border border-slate-700"
                        />
                      )}
                      
                      {/* Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-white">
                            {request.campaign_name || `${brand?.name} Campaign`}
                          </h3>
                          <div className={`flex items-center gap-2 px-3 py-1 rounded-full border text-sm font-medium ${getStatusColor(request.status)}`}>
                            {getStatusIcon(request.status)}
                            {request.status}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-slate-500">Date</p>
                            <p className="text-white font-medium">{formatDate(request.date)}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Slot</p>
                            <p className="text-white font-medium">{slot?.name}</p>
                            <p className="text-slate-400 text-xs">{slot?.time}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Cohort</p>
                            <p className="text-white font-medium">{cohort?.name}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Submitted</p>
                            <p className="text-white font-medium">{formatDate(request.created_at)}</p>
                          </div>
                        </div>

                        {request.content_text && (
                          <div className="mt-3 p-3 bg-slate-900 rounded-lg">
                            <p className="text-sm text-slate-300 line-clamp-2">{request.content_text}</p>
                          </div>
                        )}

                        {request.note && (
                          <div className="mt-3">
                            <p className="text-xs text-slate-500">Note: <span className="text-slate-400">{request.note}</span></p>
                          </div>
                        )}
                        
                        {request.drive_link && (
                          <div className="mt-3">
                            <a 
                              href={request.drive_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs font-medium hover:underline inline-flex items-center gap-1"
                              style={{ color: '#32B4AC' }}
                            >
                              🔗 Open Drive Link
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </a>
                          </div>
                        )}
                        
                        {request.creative && (
                          <div className="mt-3 flex gap-2">
                            <a
                              href={request.creative}
                              download
                              className="px-3 py-1 rounded-lg text-xs font-medium hover:shadow-md transition-all"
                              style={{ backgroundColor: 'rgba(50, 180, 172, 0.15)', color: '#32B4AC' }}
                            >
                              📥 Download Creative
                            </a>
                          </div>
                        )}
                        
                        {/* Show rejection reason if rejected */}
                        {request.status === 'Rejected' && request.rejection_reason && (
                          <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                            <p className="text-sm font-semibold text-red-300 mb-1">⚠️ Rejection Reason:</p>
                            <p className="text-sm text-red-200">{request.rejection_reason}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-2">
                      {/* Only show Edit button if not Approved AND not viewer */}
                      {request.status !== 'Approved' && !isViewOnly && (
                        <button
                          onClick={() => handleEdit(request)}
                          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition-all duration-200 hover:shadow-lg active:scale-95 justify-center"
                        >
                          <Edit2 size={16} />
                          Update
                        </button>
                      )}
                      
                      {/* Show Resend button only for rejected requests AND not viewer */}
                      {request.status === 'Rejected' && !isViewOnly && (
                        <button
                          onClick={() => handleResend(request.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition-all duration-200 hover:shadow-lg active:scale-95 justify-center"
                        >
                          Resend
                        </button>
                      )}

                      {/* Delete button: owner (non-viewer) can remove any non-Approved
                          request; admin can delete anything (enforced server-side too). */}
                      {!isViewOnly && request.status !== 'Approved' && (
                        <button
                          onClick={() => handleDelete(request)}
                          data-testid={`delete-request-${request.id}`}
                          title="Delete request"
                          className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-red-50 text-red-600 border border-red-200 hover:border-red-400 rounded-xl transition-all duration-200 hover:shadow-sm active:scale-95 justify-center"
                        >
                          <Trash2 size={16} />
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyRequests;
